<template>
    <div class="login-form-container">
        <div class="login-header">
            <h2 class="login-title">登录</h2>
            <div class="close-btn" @click="handleClose">×</div>
        </div>

        <!-- 主要内容区域：左右布局 -->
        <div class="login-main">
            <!-- 左侧：微信扫码区域 (仅PC端显示) -->
            <div v-if="!isMobile" class="login-left">
                <div class="wechat-section">
                    <!-- PC端：先显示用户协议，勾选后才显示二维码 -->
                    <div class="agreement-first">
                        <div v-if="!agreeToTerms" class="agreement-prompt">
                            <div class="agreement-header">
                                <div class="agreement-icon">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#3b82f6"/>
                                    </svg>
                                </div>
                                <h3 class="agreement-title">使用须知</h3>
                            </div>
                            <p class="agreement-desc">为了给您提供更好的服务，请您仔细阅读并同意我们的用户协议。</p>
                            <div class="agreement-checkbox-wrapper">
                                <el-checkbox v-model="agreeToTerms" class="terms-checkbox" size="large">
                                    <span class="checkbox-text">
                                        我已阅读并同意
                                        <a href="/user_agreement.html" target="_blank" class="agreement-link">《用户协议》</a>
                                    </span>
                                </el-checkbox>
                            </div>
                        </div>
                        <div v-else class="wechat-login">
                            <WechatLoginQR @login-success="handleWechatLoginSuccess" @login-failed="handleWechatLoginFailed"
                                @status-change="handleWechatStatusChange" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- 右侧：其他登录方式 -->
            <div class="login-right">
                <!-- 登录方式选择标签（移动端只显示账号登录和手机登录） -->
                <div class="login-tabs">
                    <div class="tab-item" :class="{ active: activeTab === 'password' }" @click="activeTab = 'password'">
                        账号登录
                    </div>
                    <div class="tab-item" :class="{ active: activeTab === 'phone' }" @click="activeTab = 'phone'">
                        手机登录
                    </div>
                    <!-- PC端才显示邮箱登录 -->
                    <div v-if="!isMobile" class="tab-item" :class="{ active: activeTab === 'email' }" @click="activeTab = 'email'">
                        邮箱登录
                    </div>
                </div>

                <div class="login-body">
                    <!-- 用户名密码登录 -->
                    <div v-if="activeTab === 'password'" class="login-form">
                        <div class="form-content">
                            <div class="form-group">
                                <label for="username">用户名</label>
                                <el-input id="username" v-model="formData.username" placeholder="请输入用户名"
                                    :prefix-icon="User" />
                            </div>

                            <div class="form-group">
                                <label for="password">密码</label>
                                <el-input id="password" v-model="formData.password" type="password" placeholder="请输入密码"
                                    :prefix-icon="Lock" @keyup.enter="handlePasswordLogin" />
                            </div>

                            <div class="form-options">
                                <el-checkbox v-model="formData.rememberMe">记住我</el-checkbox>
                            </div>

                            <!-- 移动端用户协议放在content底部 -->
                            <div v-if="isMobile" class="form-options">
                                <el-checkbox v-model="agreeToTerms" class="terms-checkbox">
                                    我已阅读并同意<a href="/user_agreement.html" target="_blank">《用户协议》</a>
                                </el-checkbox>
                            </div>
                        </div>

                        <div class="form-footer">
                            <el-button type="primary" class="login-btn" :loading="loading" @click="handlePasswordLogin">
                                登录
                            </el-button>
                        </div>
                    </div>

                    <!-- 手机验证码登录 -->
                    <div v-if="activeTab === 'phone'" class="login-form">
                        <div class="form-content">
                            <div class="form-group">
                                <label for="phone">手机号</label>
                                <el-input id="phone" v-model="formData.phone" placeholder="请输入手机号" :prefix-icon="Iphone"
                                    maxlength="11" />
                            </div>

                            <div class="form-group">
                                <label for="phoneCode">验证码</label>
                                <div class="code-input-group">
                                    <el-input id="phoneCode" v-model="formData.phoneCode" placeholder="请输入验证码"
                                        :prefix-icon="Key" @keyup.enter="handlePhoneLogin" />
                                    <el-button
                                        :disabled="verificationCodeState.loading || verificationCodeState.countdown > 0"
                                        @click="sendPhoneCode">
                                        {{ verificationCodeState.countdown > 0 ? `${verificationCodeState.countdown}s` :
                                            '发送验证码' }}
                                    </el-button>
                                </div>
                            </div>

                            <!-- 移动端用户协议放在content底部 -->
                            <div v-if="isMobile" class="form-options">
                                <el-checkbox v-model="agreeToTerms" class="terms-checkbox">
                                    我已阅读并同意<a href="/user_agreement.html" target="_blank">《用户协议》</a>
                                </el-checkbox>
                            </div>
                        </div>

                        <div class="form-footer">
                            <el-button type="primary" class="login-btn" :loading="loading" @click="handlePhoneLogin">
                                登录
                            </el-button>
                        </div>
                    </div>

                    <!-- 邮箱验证码登录 (仅PC端显示) -->
                    <div v-if="activeTab === 'email' && !isMobile" class="login-form">
                        <div class="form-content">
                            <div class="form-group">
                                <label for="email">邮箱</label>
                                <el-input id="email" v-model="formData.email" placeholder="请输入邮箱" :prefix-icon="Message" />
                            </div>

                            <div class="form-group">
                                <label for="emailCode">验证码</label>
                                <div class="code-input-group">
                                    <el-input id="emailCode" v-model="formData.emailCode" placeholder="请输入验证码"
                                        :prefix-icon="Key" @keyup.enter="handleEmailLogin" />
                                    <el-button
                                        :disabled="verificationCodeState.loading || verificationCodeState.countdown > 0"
                                        @click="sendEmailCode">
                                        {{ verificationCodeState.countdown > 0 ? `${verificationCodeState.countdown}s` :
                                            '发送验证码' }}
                                    </el-button>
                                </div>
                            </div>
                        </div>

                        <div class="form-footer">
                            <el-button type="primary" class="login-btn" :loading="loading" @click="handleEmailLogin">
                                登录
                            </el-button>
                        </div>
                    </div>


                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { User, Lock, Iphone, Message, Key } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import { useUserStore } from '@/stores/userStore';
import WechatLoginQR from './WechatLoginQR.vue';

export default {
    name: 'LoginForm',
    components: {
        WechatLoginQR
    },
    emits: ['login-success', 'close'],
    setup(props, { emit }) {
        const activeTab = ref('password');
        const agreeToTerms = ref(false);
        const loading = ref(false);

        // 使用用户状态管理
        const userStore = useUserStore();

        // 从store中获取状态
        const verificationCodeState = computed(() => userStore.verificationCode);
        const wechatLoginState = computed(() => userStore.wechatLogin);

        // 移动端判断
        const isMobile = computed(() => {
            return window.innerWidth <= 900;
        });

        // 表单数据
        const formData = reactive({
            username: '',
            password: '',
            phone: '',
            phoneCode: '',
            email: '',
            emailCode: '',
            rememberMe: false
        });

        // 开发环境判断
        const isDevelopment = computed(() => {
            return process.env.NODE_ENV === 'development';
        });

        // 组件挂载时检查是否有存储的用户名
        onMounted(() => {
            const savedUsername = localStorage.getItem('userName');
            if (savedUsername) {
                formData.username = savedUsername;
                formData.rememberMe = true;
            }
            
            // 移动端默认选择手机登录
            if (isMobile.value) {
                activeTab.value = 'phone';
            }
        });

        // 用户名密码登录
        const handlePasswordLogin = async () => {
            if (!formData.username || !formData.password) {
                ElMessage.warning('请输入用户名和密码');
                return;
            }
            if (!agreeToTerms.value) {
                ElMessage.warning('请先阅读并同意《用户协议》');
                return;
            }

            try {
                const result = await userStore.loginUser(formData.username, formData.password, formData.rememberMe);
                if (result) {
                    ElMessage.success('登录成功');

                    // 如果记住我被选中，保存用户名
                    if (formData.rememberMe) {
                        localStorage.setItem('userName', formData.username);
                    } else {
                        localStorage.removeItem('userName');
                    }

                    emit('login-success', formData.username);
                    resetForm();
                }
            } catch (error) {
                // 优先使用友好的错误消息，否则使用默认消息
                const errorMessage = error.friendlyMessage || error.message || '登录失败，请重试';
                ElMessage.error(errorMessage);
            }
        };

        // 发送手机验证码
        const sendPhoneCode = async () => {
            if (!formData.phone) {
                ElMessage.warning('请输入手机号');
                return;
            }
            if (!/^1[3456789]\d{9}$/.test(formData.phone)) {
                ElMessage.warning('请输入正确的手机号');
                return;
            }

            try {
                await userStore.sendCode('phone', formData.phone, 'login');
                ElMessage.success('验证码发送成功');
            } catch (error) {
                // 优先使用友好的错误消息，否则使用默认消息
                const errorMessage = error.friendlyMessage || error.message || '验证码发送失败';
                ElMessage.error(errorMessage);
            }
        };

        // 手机验证码登录
        const handlePhoneLogin = async () => {
            if (!formData.phone || !formData.phoneCode) {
                ElMessage.warning('请输入手机号和验证码');
                return;
            }
            if (!agreeToTerms.value) {
                ElMessage.warning('请先阅读并同意《用户协议》');
                return;
            }

            try {
                const result = await userStore.loginWithPhoneCode(formData.phone, formData.phoneCode, formData.rememberMe);
                if (result) {
                    ElMessage.success('登录成功');
                    emit('login-success', formData.phone);
                    resetForm();
                }
            } catch (error) {
                // 优先使用友好的错误消息，否则使用默认消息
                const errorMessage = error.friendlyMessage || error.message || '登录失败，请重试';
                ElMessage.error(errorMessage);
            }
        };

        // 发送邮箱验证码
        const sendEmailCode = async () => {
            if (!formData.email) {
                ElMessage.warning('请输入邮箱');
                return;
            }
            if (!/\S+@\S+\.\S+/.test(formData.email)) {
                ElMessage.warning('请输入正确的邮箱格式');
                return;
            }

            try {
                await userStore.sendCode('email', formData.email, 'login');
                ElMessage.success('验证码发送成功');
            } catch (error) {
                // 优先使用友好的错误消息，否则使用默认消息
                const errorMessage = error.friendlyMessage || error.message || '验证码发送失败';
                ElMessage.error(errorMessage);
            }
        };

        // 邮箱验证码登录
        const handleEmailLogin = async () => {
            if (!formData.email || !formData.emailCode) {
                ElMessage.warning('请输入邮箱和验证码');
                return;
            }
            if (!agreeToTerms.value) {
                ElMessage.warning('请先阅读并同意《用户协议》');
                return;
            }

            try {
                const result = await userStore.loginWithEmailCode(formData.email, formData.emailCode, formData.rememberMe);
                if (result) {
                    ElMessage.success('登录成功');
                    emit('login-success', formData.email);
                    resetForm();
                }
            } catch (error) {
                // 优先使用友好的错误消息，否则使用默认消息
                const errorMessage = error.friendlyMessage || error.message || '登录失败，请重试';
                ElMessage.error(errorMessage);
            }
        };

        // 微信登录成功处理
        const handleWechatLoginSuccess = (userData) => {
            ElMessage.success('微信登录成功');
            emit('login-success', userData.username || '微信用户');
            resetForm();
        };

        // 微信登录失败处理
        const handleWechatLoginFailed = (error) => {
            ElMessage.error(error || '微信登录失败');
        };

        // 微信登录状态变化处理
        const handleWechatStatusChange = (status, data) => {
            console.log('微信登录状态变化:', status, data);
        };

        // 重置表单
        const resetForm = () => {
            formData.password = '';
            formData.phoneCode = '';
            formData.emailCode = '';
        };

        // 关闭弹窗
        const handleClose = () => {
            emit('close');
        };

        onUnmounted(() => {
            // 组件卸载时的清理工作
        });

        return {
            activeTab,
            agreeToTerms,
            loading,
            formData,
            verificationCodeState,
            wechatLoginState,
            isDevelopment,
            isMobile,
            handlePasswordLogin,
            sendPhoneCode,
            handlePhoneLogin,
            sendEmailCode,
            handleEmailLogin,
            handleWechatLoginSuccess,
            handleWechatLoginFailed,
            handleWechatStatusChange,
            handleClose,
            User,
            Lock,
            Iphone,
            Message,
            Key
        };
    }
};
</script>

<style scoped>
.login-form-container {
    background: rgba(15, 23, 42, 0.95);
    color: white;
    width: 90vw;
    /* 使用视口宽度的90% */
    max-width: 750px;
    /* 增加最大宽度 */
    height: 500px;
    /* 设置固定高度 */
    overflow: hidden;
    position: relative;
    z-index: 3500;
    border-radius: 16px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
}

.login-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 25px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    position: relative;
    z-index: 3600;
}

.login-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    background: linear-gradient(90deg, #3b82f6, #60a5fa);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
}

.close-btn {
    font-size: 1.8rem;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.6);
    transition: color 0.3s, transform 0.2s;
    z-index: 3700;
    position: relative;
}

.close-btn:hover {
    color: #3b82f6;
    transform: scale(1.1);
}

/* 主要内容区域：左右布局 */
.login-main {
    display: flex;
    flex: 1;
    /* 占满剩余空间 */
    height: 100%;
    /* 确保高度稳定 */
}

/* 左侧微信扫码区域 */
.login-left {
    flex: 0 0 40%;
    /* 调整左侧宽度占比 */
    padding: 20px;
    border-right: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    /* 固定高度 */
    overflow: hidden;
}

.wechat-section {
    text-align: center;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.section-title {
    margin: 0 0 20px 0;
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
}

/* PC端用户协议优先显示 */
.agreement-first {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
}

.agreement-prompt {
    text-align: center;
    padding: 30px 10px;
    max-width: 100%;
    margin: 0 auto;
}

.agreement-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 20px;
}

.agreement-icon {
    margin-bottom: 12px;
    opacity: 0.9;
}

.agreement-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    background: linear-gradient(135deg, #3b82f6, #60a5fa);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
}

.agreement-desc {
    margin: 0 0 25px 0;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.6;
    text-align: center;
    word-break: break-word;
}

.agreement-checkbox-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
}

.terms-checkbox {
    margin: 0;
}

.checkbox-text {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
    margin-left: 8px;
}

.agreement-link {
    color: #3b82f6;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s ease;
}

.agreement-link:hover {
    color: #60a5fa;
    text-decoration: underline;
}

/* 右侧其他登录方式区域 */
.login-right {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    /* 固定高度 */
    overflow: hidden;
}

.login-tabs {
    display: flex;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    margin: 0;
}

.tab-item {
    flex: 1;
    text-align: center;
    padding: 12px 8px;
    cursor: pointer;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.6);
    transition: all 0.3s;
    border-bottom: 2px solid transparent;
}

.tab-item:hover {
    color: rgba(255, 255, 255, 0.8);
}

.tab-item.active {
    color: #3b82f6;
    border-bottom-color: #3b82f6;
}

.login-body {
    padding: 20px;
    position: relative;
    z-index: 3500;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
}

.login-form {
    display: flex;
    flex-direction: column;
    flex: 1;
}

.form-content {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.form-footer {
    margin-top: auto;
    padding-top: 20px;
}

.form-footer .form-options {
    margin-bottom: 15px;
}

.form-footer .terms-checkbox {
    margin: 0;
}

.form-footer .form-options a {
    color: #3b82f6;
    text-decoration: none;
}

.form-footer .form-options a:hover {
    text-decoration: underline;
}

.form-group {
    margin-bottom: 20px;
    width: 100%;
    box-sizing: border-box;
}

.code-input-group {
    display: flex;
    gap: 10px;
    width: 100%;
    box-sizing: border-box;
}

.form-options {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 25px;
    font-size: 0.9rem;
    width: 100%;
    box-sizing: border-box;
}

.agreement-section {
    margin-top: 20px;
    margin-bottom: 0;
}

.terms-checkbox {
    margin-top: 2px;
}

.form-options a {
    color: #3b82f6;
    text-decoration: none;
}

.form-options a:hover {
    text-decoration: underline;
}

.form-footer {
    margin-top: auto;
    padding-top: 20px;
}

.form-footer .form-options {
    margin-bottom: 15px;
}

.login-btn {
    width: 100%;
    padding: 12px;
    height: 44px;
    background: linear-gradient(90deg, #3b82f6, #60a5fa);
    border: none;
    font-weight: 600;
    font-size: 1rem;
    transition: all 0.3s ease;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.login-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
}

.login-btn:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
}

/* 微信登录样式 */
.wechat-login {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.qr-placeholder {
    padding: 40px 20px;
}

.init-wechat-btn {
    background: #07c160;
    border-color: #07c160;
}

.init-wechat-btn:hover {
    background: #06a552;
    border-color: #06a552;
}

.qr-code-container {
    width: 100%;
}

.qr-status-info {
    margin-bottom: 15px;
}

.status-text {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
}

.qr-code-wrapper {
    position: relative;
    display: inline-block;
    margin-bottom: 15px;
}

.qr-code-image {
    width: 200px;
    height: 200px;
    border-radius: 8px;
    border: 2px solid rgba(255, 255, 255, 0.1);
}

.qr-expired-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    color: white;
}

.qr-tip {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
}

/* Element Plus 样式覆盖 */
:deep(.el-input) {
    width: 100%;
}

:deep(.el-input__wrapper) {
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1) inset;
    width: 100%;
    box-sizing: border-box;
}

:deep(.el-input__wrapper:hover) {
    box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.5) inset;
}

:deep(.el-input__wrapper.is-focus) {
    box-shadow: 0 0 0 1px #3b82f6 inset !important;
}

:deep(.el-input__inner) {
    color: white;
}

:deep(.el-input__prefix-inner) {
    color: rgba(255, 255, 255, 0.7);
}

:deep(.el-checkbox) {
    width: 100%;
    box-sizing: border-box;
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
    background-color: #3b82f6;
    border-color: #3b82f6;
}

:deep(.el-checkbox__label) {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.4;
    word-wrap: break-word;
}

:deep(.el-button) {
    box-sizing: border-box;
}

:deep(.el-button--small) {
    padding: 6px 12px;
}

/* 优化用户协议复选框样式 */
:deep(.agreement-checkbox-wrapper .el-checkbox) {
    align-items: center;
}

:deep(.agreement-checkbox-wrapper .el-checkbox__inner) {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 4px;
    background: transparent;
    transition: all 0.3s ease;
}

:deep(.agreement-checkbox-wrapper .el-checkbox__inner:hover) {
    border-color: rgba(59, 130, 246, 0.6);
}

:deep(.agreement-checkbox-wrapper .el-checkbox__input.is-checked .el-checkbox__inner) {
    background: linear-gradient(135deg, #3b82f6, #60a5fa);
    border-color: #3b82f6;
    transform: scale(1.05);
}

:deep(.agreement-checkbox-wrapper .el-checkbox__inner::after) {
    border-width: 2px;
    border-color: white;
}

:deep(.agreement-checkbox-wrapper .el-checkbox__label) {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
    padding-left: 8px;
}

/* 响应式设计 */
@media (max-width: 900px) {
    .login-form-container {
        width: 90vw;
        max-width: 380px;
        height: 540px;
        /* 移动端增加高度，确保登录按钮完全显示 */
        margin: 0 auto;
    }

    .login-main {
        flex-direction: column;
        width: 100%;
    }

    .login-left {
        display: none;
    }

    .login-right {
        flex: 1;
        height: auto;
        padding-top: 0;
        border-left: none;
        width: 100%;
    }

    .section-title {
        font-size: 1rem;
        margin-bottom: 15px;
    }

    /* 移动端用户协议样式调整 */
    .agreement-prompt {
        padding: 20px 15px;
        max-width: 100%;
    }

    .agreement-title {
        font-size: 1.1rem;
    }

    .agreement-desc {
        font-size: 0.85rem;
        margin-bottom: 20px;
        white-space: normal;
        word-break: break-word;
    }

    /* 移动端表单布局调整 */
    .login-body {
        padding: 10px;
        width: 100%;
        box-sizing: border-box;
    }

    .form-content .form-options:last-child {
        margin-bottom: 5px;
    }

    .form-options {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: 8px;
        font-size: 0.9rem;
        width: 100%;
        box-sizing: border-box;
    }

    .login-form {
        width: 100%;
    }

    .form-content {
        width: 100%;
    }

    .form-group {
        width: 100%;
        margin-bottom: 14px;
    }

    .code-input-group {
        width: 100%;
        gap: 8px;
    }

    .code-input-group .el-input {
        flex: 1;
        min-width: 0;
    }

    .code-input-group .el-button {
        flex-shrink: 0;
        padding: 8px 12px;
        font-size: 0.85rem;
    }

    .form-footer {
        padding-top: 5px;
        width: 100%;
        margin-top: 5px;
    }

    .form-footer .form-options {
        margin-bottom: 8px;
    }

    .login-btn {
        height: 42px;
        font-size: 0.95rem;
        width: 100%;
    }

    /* 移动端Element Plus组件样式重写 */
    :deep(.el-input) {
        width: 100%;
    }

    :deep(.el-input__wrapper) {
        width: 100%;
        box-sizing: border-box;
    }

    :deep(.el-button) {
        box-sizing: border-box;
    }

    :deep(.el-checkbox) {
        width: 100%;
        box-sizing: border-box;
    }

    :deep(.el-checkbox__label) {
        font-size: 0.9rem;
        line-height: 1.4;
        white-space: normal;
        word-break: break-word;
    }
}

@media (max-width: 480px) {
    .login-form-container {
        width: 95vw;
        max-width: 320px;
        height: 480px;
        /* 小屏幕也需要增加高度 */
        margin: 0 auto;
    }

    .login-left,
    .login-body {
        padding: 10px;
        width: 100%;
        box-sizing: border-box;
    }

    .tab-item {
        padding: 8px 4px;
        font-size: 0.8rem;
    }

    .section-title {
        font-size: 0.9rem;
        margin-bottom: 10px;
    }

    /* 小屏幕用户协议样式调整 */
    .agreement-title {
        font-size: 1rem;
    }

    .agreement-desc {
        font-size: 0.8rem;
        margin-bottom: 15px;
        white-space: normal;
        word-break: break-word;
    }

    .checkbox-text {
        font-size: 0.8rem;
    }

    .agreement-link {
        font-size: 0.8rem;
    }

    /* 小屏幕表单布局调整 */
    .form-group {
        margin-bottom: 14px;
    }

    .code-input-group {
        gap: 6px;
    }

    .code-input-group .el-button {
        padding: 6px 8px;
        font-size: 0.8rem;
        min-width: 80px;
    }

    .form-footer {
        padding-top: 5px;
    }

    .form-footer .form-options {
        margin-bottom: 8px;
    }

    .login-btn {
        height: 40px;
        font-size: 0.9rem;
    }

    /* 小屏幕Element Plus组件样式重写 */
    :deep(.el-input__wrapper) {
        padding: 8px 10px;
    }

    :deep(.el-checkbox__label) {
        font-size: 0.8rem;
        line-height: 1.3;
    }
}
</style>